import { useEffect, useState, useRef } from "react"
import figmaLogo from "../assets/figma.webp"
import riotLogo from "../assets/riot.webp"
import scaleLogo from "../assets/sca;e.webp"
import slbLogo from "../assets/slb.webp"
import spluckLogo from "../assets/spluck.webp"
import travleLogo from "../assets/travle.webp"
import webflowLogo from "../assets/webflow.webp"
import { useNavigate } from "react-router-dom"

const STEPS = [
  {
    id: 1,
    label: "Parsing Transcript",
    sub: "Extracting Q&A pairs from interview",
  },
  {
    id: 2,
    label: "Scoring Responses",
    sub: "Evaluating depth, clarity & relevance",
  },
  {
    id: 3,
    label: "Detecting Patterns",
    sub: "Identifying strengths & red flags",
  },
  {
    id: 4,
    label: "Compiling Report",
    sub: "Assembling final candidate summary",
  },
]

const STEP_TIMINGS = [0, 8000, 16000, 24000]

const FEATURES = [
  {
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 4h12M2 8h8M2 12h5" />
      </svg>
    ),
    label: "Automated Resume Screening",
    sub: "Process hundreds of resumes in seconds",
  },
  {
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="8" cy="8" r="6" />
        <path d="M8 5v3l2 2" />
      </svg>
    ),
    label: "AI-Powered Interviews",
    sub: "AI runs the first round, AI agents helps interviewers probe deeper in subsequent rounds",
  },
  {
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 12s2-4 6-4 6 4 6 4" />
        <circle cx="8" cy="5" r="2.5" />
      </svg>
    ),
    label: "Built-in Cheating Detection",
    sub: "Candidates use AI. We detect it",
  },
  {
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 2L2 6v8h4v-4h4v4h4V6L8 2z" />
      </svg>
    ),
    label: "Skill-by-Skill Scoring",
    sub: "Granular breakdown across every competency",
  },
]

// Smooth progress: fast start, decelerates after 80%, hard stops at 98%
function useSmoothedProgress() {
  const [progress, setProgress] = useState(0)
  const frameRef = useRef(null)

  useEffect(() => {
    const DURATION_MS = 50000
    const start = performance.now()

    const tick = (now) => {
      const t = Math.min((now - start) / DURATION_MS, 1)

      let pct
      if (t <= 0.6) {
        // Phase 1: easeOutCubic → reaches ~80 at t=0.6
        const t1 = t / 0.6
        pct = 80 * (1 - Math.pow(1 - t1, 3))
      } else {
        // Phase 2: easeOutExpo → creeps from 80 to 98
        const t2 = (t - 0.6) / 0.4
        pct = 80 + 18 * (1 - Math.pow(2, -10 * t2))
      }

      pct = Math.min(pct, 98)
      setProgress(+pct.toFixed(1))

      if (pct < 98) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [])

  return progress
}

export default function ReportGenerating({ phase }) {
  const [activeStep, setActiveStep] = useState(0)
  const [seconds, setSeconds] = useState(32)
  const progress = useSmoothedProgress()

  const navigate = useNavigate()

  useEffect(() => {
    const timers = STEP_TIMINGS.map((delay, i) =>
      setTimeout(() => setActiveStep(i), delay),
    )
    return () => timers.forEach(clearTimeout)
  }, [])

  useEffect(() => {
    if (seconds <= 0) return
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [seconds])

  const [showModal, setShowModal] = useState(false)
  const isError = phase === "error"

  return (
    <div
      className="min-h-screen flex flex-col bg-zinc-50"
      style={{ fontFamily: "'Satoshi', sans-serif" }}
    >
      <style>{`
        @import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,600,700&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.2} }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp .4s ease both; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .marquee-track { display: flex; animation: marquee 24s linear infinite; width: max-content; align-items: center; }
      `}</style>

      {/* ── Nav ── */}
      <div className="bg-white border-b border-zinc-200 shrink-0 z-20">
        <div className="px-32 py-3 flex items-center justify-between">
          <span
            onClick={() => navigate("/")}
            className="text-lg font-bold text-zinc-900 tracking-tight cursor-pointer hover:opacity-70 transition-opacity"
          >
            Resolut
          </span>
          <button
            onClick={() => setShowModal(true)}
            className="bg-zinc-900 text-white text-[11px] font-bold px-4 py-2 uppercase tracking-widest hover:bg-zinc-700 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Book Demo
          </button>
        </div>
      </div>

      {/* ── Progress banner ── */}
      <div className="bg-white border-b border-zinc-200 shrink-0">
        <div className="px-32 py-4 flex items-center gap-6">
          <p className="text-[12px] font-bold uppercase tracking-widest text-zinc-900 shrink-0">
            {isError ? "Failed to Load Report" : "Creating your report"}
          </p>
          <div className="flex-1 h-0.5 bg-zinc-200">
            <div
              style={{
                height: "100%",
                width: isError ? "100%" : `${progress}%`,
                background: isError ? "#dc2626" : "#18181b",
                transition: "width .6s ease-out",
              }}
            />
          </div>
          {!isError && (
            <span className="text-[12px] font-bold text-zinc-500 shrink-0">
              {Math.round(progress)}%
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1" style={{ minHeight: "calc(100vh - 260px)" }}>
        {/* ════ LEFT — company marketing ════ */}
        <div
          className="flex flex-col justify-center px-32 py-8 gap-8"
          style={{ width: "50%" }}
        >
          {/* Headline */}
          <div className="fade-up" style={{ animationDelay: "0ms" }}>
            <p className="text-[13px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
              Learn more about Resolut
            </p>
            <h2 className="text-3xl font-bold text-zinc-900 leading-snug">
              Find 10x engineers,{" "}
              <span className="text-zinc-500 font-normal">at 10x speed</span>
            </h2>
            <p className="text-[15px] text-zinc-600 mt-3 leading-relaxed max-w-sm">
              Resolut automates your entire hiring pipeline — from screening to
              onboarding, so your team focuses on decisions, not logistics.
            </p>
          </div>

          {/* Feature list */}
          <div
            className="flex flex-col divide-y divide-zinc-100 border border-zinc-200 bg-white fade-up"
            style={{ animationDelay: "80ms", borderRadius: 0 }}
          >
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-4">
                <div
                  className="w-7 h-7 shrink-0 flex items-center justify-center mt-0.5"
                  style={{
                    background: "#f4f4f5",
                    border: "1px solid #d4d4d8",
                    borderRadius: 0,
                    color: "#3f3f46",
                  }}
                >
                  {f.icon}
                </div>
                <div>
                  <p className="text-[13px] font-bold uppercase tracking-widest text-zinc-800">
                    {f.label}
                  </p>
                  <p className="text-[14px] text-zinc-500 mt-0.5 leading-snug">
                    {f.sub}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ════ RIGHT — steps card ════ */}
        <div
          className="flex items-center justify-center px-16 py-8 border-l border-zinc-200"
          style={{ width: "50%" }}
        >
          <div className="w-full" style={{ maxWidth: 460 }}>
            {isError ? (
              <div
                className="bg-white border border-zinc-200 px-7 py-6"
                style={{ borderRadius: 0 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-4 h-4 shrink-0 flex items-center justify-center"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                    }}
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 16 16"
                      fill="#dc2626"
                    >
                      <path d="M8 1L1 14h14L8 1zm-.75 5h1.5v4h-1.5V6zm0 5h1.5v1.5h-1.5V11z" />
                    </svg>
                  </div>
                  <p className="text-[12px] font-bold uppercase tracking-widest text-zinc-900">
                    Failed to Load Report
                  </p>
                </div>
                <p className="text-[14px] text-zinc-600 leading-relaxed">
                  Something went wrong while analyzing the interview. Please
                  refresh the page or contact support if the issue persists.
                </p>
              </div>
            ) : (
              <div
                className="bg-white border border-zinc-200"
                style={{ borderRadius: 0 }}
              >
                {/* Steps header */}
                <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-3.5 h-3.5 border-2 border-zinc-200"
                      style={{
                        borderTopColor: "#18181b",
                        borderRadius: "50%",
                        animation: "spin .7s linear infinite",
                      }}
                    />
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-600">
                      Analyzing Interview
                    </p>
                  </div>
                  <span className="text-[11px] font-bold text-zinc-500">
                    {seconds > 0 ? `~${seconds}s` : "Almost done…"}
                  </span>
                </div>

                {/* Steps list */}
                <div className="divide-y divide-zinc-100">
                  {STEPS.map((step, i) => {
                    const isDone = i < activeStep
                    const isActive = i === activeStep
                    const isWaiting = i > activeStep

                    return (
                      <div
                        key={step.id}
                        className="flex items-center gap-4 px-6 py-4 transition-opacity duration-500"
                        style={{ opacity: isWaiting ? 0.35 : 1 }}
                      >
                        {/* Index box */}
                        <div
                          className="w-6 h-6 shrink-0 flex items-center justify-center"
                          style={{
                            borderRadius: 0,
                            ...(isDone
                              ? {
                                  background: "#f0fdf4",
                                  border: "1px solid #bbf7d0",
                                }
                              : isActive
                                ? { background: "#18181b" }
                                : {
                                    background: "#f4f4f5",
                                    border: "1px solid #e4e4e7",
                                  }),
                          }}
                        >
                          {isDone ? (
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="#16a34a"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 8l3.5 3.5L13 5" />
                            </svg>
                          ) : (
                            <span
                              className="text-[10px] font-bold"
                              style={{
                                color: isActive ? "#fff" : "#71717a",
                              }}
                            >
                              {String(step.id).padStart(2, "0")}
                            </span>
                          )}
                        </div>

                        {/* Label + sub */}
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-[12px] font-bold uppercase tracking-widest leading-none"
                            style={{
                              color: isActive
                                ? "#18181b"
                                : isDone
                                  ? "#3f3f46"
                                  : "#71717a",
                            }}
                          >
                            {step.label}
                          </p>
                          <p
                            className="text-[13px] mt-1 leading-snug"
                            style={{ color: isActive ? "#52525b" : "#a1a1aa" }}
                          >
                            {step.sub}
                          </p>
                        </div>

                        {/* Badge */}
                        <span
                          className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 shrink-0"
                          style={{
                            borderRadius: 0,
                            ...(isDone
                              ? {
                                  background: "#f0fdf4",
                                  border: "1px solid #bbf7d0",
                                  color: "#15803d",
                                }
                              : isActive
                                ? { background: "#18181b", color: "#fff" }
                                : {
                                    background: "#f4f4f5",
                                    border: "1px solid #e4e4e7",
                                    color: "#71717a",
                                  }),
                          }}
                        >
                          {isDone ? "Done" : isActive ? "Running" : "Queued"}
                        </span>
                      </div>
                    )
                  })}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50 flex items-center gap-2">
                  <div
                    className="w-1.5 h-1.5 bg-zinc-900 shrink-0"
                    style={{
                      borderRadius: "50%",
                      animation: "blink 1.3s ease-in-out infinite",
                    }}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                    Report will load automatically when ready
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Trusted by logos ── */}
      <div className="relative w-full bg-white overflow-hidden border-t border-gray-100">
        <div className="px-32">
          <div className="flex items-center" style={{ height: "100px" }}>
            {/* Static "Trusted by" */}
            <div className="flex flex-shrink-0 items-center pr-6 z-20">
              <p className="text-[12px] font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap">
                Trusted by global teams
              </p>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200 mr-6 flex-shrink-0" />

            {/* Scrolling logos */}
            <div className="relative flex-1 overflow-hidden h-full">
              {/* Fade edges */}
              <div
                className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
                style={{
                  background: "linear-gradient(to right, #ffffff, transparent)",
                }}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
                style={{
                  background: "linear-gradient(to left, #ffffff, transparent)",
                }}
              />

              <div className="flex items-center h-full overflow-hidden">
                <div className="marquee-track">
                  {[
                    ...[
                      { src: riotLogo, alt: "Riot Games" },
                      { src: scaleLogo, alt: "Scale" },
                      { src: slbLogo, alt: "SLB" },
                      { src: spluckLogo, alt: "Splunk" },
                      { src: travleLogo, alt: "Travelers" },
                      { src: figmaLogo, alt: "Figma" },
                      { src: webflowLogo, alt: "Webflow" },
                    ],
                    ...[
                      { src: riotLogo, alt: "Riot Games" },
                      { src: scaleLogo, alt: "Scale" },
                      { src: slbLogo, alt: "SLB" },
                      { src: spluckLogo, alt: "Splunk" },
                      { src: travleLogo, alt: "Travelers" },
                      { src: figmaLogo, alt: "Figma" },
                      { src: webflowLogo, alt: "Webflow" },
                    ],
                  ].map((logo, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 flex items-center justify-center mx-8"
                    >
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="max-h-7 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cal Booking Modal ── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white border border-zinc-200 w-full relative"
            style={{
              borderRadius: 0,
              width: "80vw",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between shrink-0">
              <p className="text-[12px] font-bold uppercase tracking-widest text-zinc-900">
                Book Product Demo
              </p>
              <button
                onClick={() => setShowModal(false)}
                className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M3 3l10 10M13 3L3 13" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
